import Constants from 'expo-constants';
import * as Sentry from '@sentry/react-native';
import { Platform } from 'react-native';

function envFlag(value: string | undefined) {
  if (!value) {
    return false;
  }

  return value === '1' || value.toLowerCase() === 'true';
}

function envNumber(value: string | undefined, fallback: number) {
  if (!value) {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function isSentryEnabled(dsn: string | undefined, enabledFlag: string | undefined) {
  if (!dsn) {
    return false;
  }

  if (enabledFlag === undefined) {
    return !__DEV__;
  }

  return envFlag(enabledFlag);
}

function isSessionReplayEnabled(enabledFlag: string | undefined) {
  return envFlag(enabledFlag);
}

function getRelease() {
  const version = Constants.expoConfig?.version ?? '0.0.0';
  const iosBuild = Constants.expoConfig?.ios?.buildNumber;
  const androidBuild = Constants.expoConfig?.android?.versionCode;
  const build = Platform.OS === 'ios' ? iosBuild : androidBuild ? String(androidBuild) : null;
  return build ? `radious-mobile@${version}+${build}` : `radious-mobile@${version}`;
}

function redactText(value: string) {
  return value
    .replace(/Bearer\s+[A-Za-z0-9._-]+/gi, 'Bearer [REDACTED]')
    .replace(/(access[_-]?token|refresh[_-]?token|password)=([^&\s]+)/gi, '$1=[REDACTED]')
    .replace(/(X-Amz-Signature|X-Amz-Credential|X-Amz-Security-Token)=([^&\s]+)/gi, '$1=[REDACTED]')
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[REDACTED_EMAIL]');
}

function sanitizeURL(value: string) {
  const redacted = redactText(value);

  try {
    const url = new URL(redacted);
    url.search = '';
    return url.toString();
  } catch {
    return redacted.replace(/\?.*$/, '?[REDACTED]');
  }
}

function sanitizeUnknown(value: unknown): unknown {
  if (typeof value === 'string') {
    return redactText(value);
  }

  if (Array.isArray(value)) {
    return value.map((entry) => sanitizeUnknown(entry));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [
        key,
        /authorization|cookie|password|token|secret|signature/i.test(key)
          ? '[REDACTED]'
          : sanitizeUnknown(entry),
      ])
    );
  }

  return value;
}

function sanitizeBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = redactText(breadcrumb.message);
  }

  if (breadcrumb.data) {
    breadcrumb.data = Object.fromEntries(
      Object.entries(breadcrumb.data).map(([key, value]) => {
        if (typeof value === 'string' && (key === 'url' || key === 'request.url')) {
          return [key, sanitizeURL(value)];
        }
        if (key === 'http.query') {
          return [key, '[REDACTED]'];
        }
        return [key, sanitizeUnknown(value)];
      })
    );
  }

  return breadcrumb;
}

function sanitizeEvent(event: Sentry.ErrorEvent) {
  if (event.request?.headers) {
    delete event.request.headers.Authorization;
    delete event.request.headers.authorization;
    delete event.request.headers.Cookie;
    delete event.request.headers.cookie;
    delete event.request.headers['X-Api-Key'];
    delete event.request.headers['x-api-key'];
  }

  if (typeof event.request?.url === 'string') {
    event.request.url = sanitizeURL(event.request.url);
  }

  if (event.request?.query_string) {
    event.request.query_string = '[REDACTED]';
  }

  if (event.request?.data) {
    event.request.data = sanitizeUnknown(event.request.data);
  }

  if (event.user?.email) {
    delete event.user.email;
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => sanitizeBreadcrumb(breadcrumb));
  }

  if (event.extra) {
    event.extra = sanitizeUnknown(event.extra) as typeof event.extra;
  }

  return event;
}

const sentryEnabled = isSentryEnabled(
  process.env.EXPO_PUBLIC_SENTRY_DSN,
  process.env.EXPO_PUBLIC_SENTRY_ENABLED,
);
const sessionReplayEnabled = isSessionReplayEnabled(
  process.env.EXPO_PUBLIC_SENTRY_REPLAY_ENABLED,
);
const tracesSampleRate = envNumber(process.env.EXPO_PUBLIC_SENTRY_TRACES_SAMPLE_RATE, 0);

Sentry.init({
  dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
  enabled: sentryEnabled,
  debug: sentryEnabled && __DEV__,
  sendDefaultPii: false,
  environment:
    process.env.EXPO_PUBLIC_SENTRY_ENVIRONMENT ?? (__DEV__ ? 'development' : 'production'),
  release: getRelease(),
  tracesSampleRate,
  replaysSessionSampleRate: sessionReplayEnabled ? 1 : 0,
  replaysOnErrorSampleRate: sessionReplayEnabled ? 1 : 0,
  beforeBreadcrumb(breadcrumb) {
    return sanitizeBreadcrumb(breadcrumb);
  },
  beforeSend(event) {
    return sanitizeEvent(event);
  },
});

export { Sentry };

type SentryPrimitive = string | number | boolean | null | undefined;

type SentryInputTrackerConfig = {
  screen: string;
  field: string;
  form?: string;
  entityId?: string | null;
  extra?: Record<string, SentryPrimitive>;
};

type SentryInputTracker = {
  focus: () => void;
  blur: (value?: string) => void;
  changeText: (value: string) => void;
  submit: (value?: string) => void;
};

function toSafeData(data?: Record<string, unknown>) {
  if (!data) {
    return undefined;
  }

  return sanitizeUnknown(data) as Record<string, unknown>;
}

function setLastInteractionContext(data: Record<string, unknown>) {
  if (!sentryEnabled) {
    return;
  }

  Sentry.setContext('last_interaction', toSafeData(data) ?? null);
}

export function addSentryBreadcrumb({
  category,
  message,
  data,
  level = 'info',
}: {
  category: string;
  message: string;
  data?: Record<string, unknown>;
  level?: Sentry.Breadcrumb['level'];
}) {
  if (!sentryEnabled) {
    return;
  }

  Sentry.addBreadcrumb({
    category,
    message,
    level,
    data: toSafeData(data),
  });
}

export function addSentryActionBreadcrumb({
  screen,
  action,
  data,
  level = 'info',
}: {
  screen: string;
  action: string;
  data?: Record<string, unknown>;
  level?: Sentry.Breadcrumb['level'];
}) {
  setLastInteractionContext({
    kind: 'action',
    screen,
    action,
    ...data,
  });
  addSentryBreadcrumb({
    category: 'ui.action',
    message: action,
    level,
    data: {
      screen,
      action,
      ...data,
    },
  });
}

export function setSentryScreen(screen: string, data?: Record<string, unknown>) {
  if (!sentryEnabled) {
    return;
  }

  Sentry.setTag('screen', screen);
  Sentry.setContext(
    'screen',
    toSafeData({
      screen,
      ...data,
    }) ?? null
  );
  setLastInteractionContext({
    kind: 'screen_view',
    screen,
    ...data,
  });
  addSentryBreadcrumb({
    category: 'navigation',
    message: 'Opened screen',
    data: {
      screen,
      ...data,
    },
  });
}

export function createSentryInputTracker({
  screen,
  field,
  form,
  entityId,
  extra,
}: SentryInputTrackerConfig): SentryInputTracker {
  let hasTypedSinceFocus = false;

  const baseData = {
    screen,
    field,
    ...(form ? { form } : {}),
    ...(entityId ? { entity_id: entityId } : {}),
    ...extra,
  };

  return {
    focus() {
      setLastInteractionContext({
        kind: 'input_focus',
        ...baseData,
      });
      addSentryBreadcrumb({
        category: 'ui.input',
        message: 'Focused input',
        data: baseData,
      });
    },
    blur(value) {
      const valueLength = typeof value === 'string' ? value.length : undefined;
      setLastInteractionContext({
        kind: 'input_blur',
        value_length: valueLength,
        ...baseData,
      });
      addSentryBreadcrumb({
        category: 'ui.input',
        message: 'Blurred input',
        data: {
          ...baseData,
          ...(valueLength !== undefined ? { value_length: valueLength } : {}),
        },
      });
      hasTypedSinceFocus = false;
    },
    changeText(value) {
      setLastInteractionContext({
        kind: 'input_edit',
        value_length: value.length,
        has_value: value.trim().length > 0,
        ...baseData,
      });

      if (value.length === 0) {
        if (!hasTypedSinceFocus) {
          return;
        }

        addSentryBreadcrumb({
          category: 'ui.input',
          message: 'Cleared input',
          data: baseData,
        });
        hasTypedSinceFocus = false;
        return;
      }

      if (hasTypedSinceFocus) {
        return;
      }

      addSentryBreadcrumb({
        category: 'ui.input',
        message: 'Started typing',
        data: {
          ...baseData,
          value_length: value.length,
        },
      });
      hasTypedSinceFocus = true;
    },
    submit(value) {
      const valueLength = typeof value === 'string' ? value.length : undefined;
      setLastInteractionContext({
        kind: 'input_submit',
        value_length: valueLength,
        ...baseData,
      });
      addSentryBreadcrumb({
        category: 'ui.input',
        message: 'Submitted input',
        data: {
          ...baseData,
          ...(valueLength !== undefined ? { value_length: valueLength } : {}),
        },
      });
    },
  };
}

export function setSentryUser(user: {
  id: string;
  username?: string | null;
} | null) {
  if (!sentryEnabled) {
    return;
  }

  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.id,
    username: user.username ?? undefined,
  });
}

export function captureSentryException(error: unknown, context?: {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
}) {
  if (!sentryEnabled) {
    return;
  }

  Sentry.withScope((scope) => {
    for (const [key, value] of Object.entries(context?.tags ?? {})) {
      scope.setTag(key, value);
    }
    for (const [key, value] of Object.entries(context?.extra ?? {})) {
      scope.setExtra(key, value);
    }
    Sentry.captureException(error);
  });
}
